// PaginationWithDateRangeAndSearch

import React, { FC, useCallback, useMemo, useState } from 'react';
import { fixPagination } from 'utils';
import { DateRangePickerProps } from 'components/DateRangePicker';
import PureSearchBar, { PureSearchBarProps } from 'components/SearchBar/PureSearchBar';
import BasicPagination from './BasicPagination';
import PaginationLimitSelect from './PaginationLimitSelect';
import { StyledDateRangePicker, useStyles } from './styles';
import { ChangeListener, PaginationLabels, Value } from './types';
import { getPaginationSetter } from './utils';
import { DEFAULT_PAGINATION_VALUE } from './constants';
import AccountTypeSelect, { AccountTypeSelectProps } from './AccountTypeSelect';

export type PaginationWithDateRangeAndSearchProps = {
  value?: Value;
  defaultValue?: Value;
  count: number;
  labels?: PaginationLabels;
  onChange?: ChangeListener;
  dateRangePickerProps: DateRangePickerProps;
  searchBarProps: PureSearchBarProps;
  accountTypeSelectProps: AccountTypeSelectProps;
};

const PaginationWithDateRangeAndSearch: FC<PaginationWithDateRangeAndSearchProps> = ({
  value,
  defaultValue,
  count = 0,
  onChange,
  labels,
  dateRangePickerProps,
  searchBarProps,
  accountTypeSelectProps,
}) => {
  const styles = useStyles();
  const [innerValue, setInnerValue] = useState(null as unknown as Value);
  const selectedValue = useMemo(
    () => value || innerValue || defaultValue || DEFAULT_PAGINATION_VALUE,
    [defaultValue, innerValue, value],
  );

  const applyChanges: ChangeListener = useCallback(
    (newValue: Value) => {
      setInnerValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const setPagination = useMemo(() => getPaginationSetter(applyChanges, selectedValue), [applyChanges, selectedValue]);

  return (
    <div className={`${styles.paginationWrapperInnerContent} ${styles.paginationWrapperInnerContentPadding}`}>
      <StyledDateRangePicker {...dateRangePickerProps} />
      <PureSearchBar {...searchBarProps} />
      <PaginationLimitSelect
        value={selectedValue.limit}
        count={count}
        label={labels?.limitSelectLabel}
        onChange={(selected): void => setPagination(fixPagination({ ...selectedValue, limit: selected }, count))}
      />
      <BasicPagination value={selectedValue} count={count} label={labels?.itemCountLabel} onChange={setPagination} />
      {!accountTypeSelectProps.isHideAccountType && <AccountTypeSelect {...accountTypeSelectProps} />}
    </div>
  );
};

export default PaginationWithDateRangeAndSearch;
